import {useCallback, useState} from 'react';

/**
 * Набор вариантов по умолчанию. 20 — столько помещается на экран без прокрутки в большинстве
 * таблиц; 100 — верхняя граница, которую бэкенд гарантированно принимает (`MAX_PAGE_SIZE`
 * контроллеров уведомлений и админских списков равен 100).
 */
export const DEFAULT_PAGE_SIZE_OPTIONS = [20, 50, 100];

export const DEFAULT_PAGE_SIZE = DEFAULT_PAGE_SIZE_OPTIONS[0];

/**
 * Ключ хранения строится от имени таблицы: размер выдачи в аудите и в архиве выбытия —
 * разные настройки, и общий ключ незаметно менял бы одну при правке другой.
 */
export const pageSizeStorageKey = (table: string): string => `clothes-pagesize-${table}`;

/**
 * Читает сохранённый размер страницы. Хранилище может быть недоступно (приватное окно,
 * запрет данных сайта) или содержать что угодно — в обоих случаях берём значение по
 * умолчанию, а не падаем и не отправляем на бэкенд мусорный `size`.
 */
export const readStoredPageSize = (
    storageKey: string,
    options: number[] = DEFAULT_PAGE_SIZE_OPTIONS,
    fallback: number = options[0],
): number => {
    try {
        const raw = localStorage.getItem(storageKey);
        if (raw) {
            const value = parseInt(raw, 10);
            if (options.includes(value)) {
                return value;
            }
        }
    } catch {
        // хранилище недоступно — берём значение по умолчанию
    }
    return fallback;
};

export interface UsePageSizeOptions {
    /** Допустимые варианты; значение вне списка считается испорченным. */
    options?: number[];
    /** Что показывать, когда сохранённого значения нет. По умолчанию — первый вариант. */
    fallback?: number;
    /** Вызывается после смены размера: страницы сбрасываются на первую (FR-4.5). */
    onChange?: (size: number) => void;
}

/**
 * Размер страницы таблицы или списка: значение и способ его сменить, с сохранением выбора
 * между сессиями (CTHS-7). Хранение — забота хука, чтобы каждая страница не повторяла
 * одни и те же try/catch вокруг localStorage.
 */
export function usePageSize(
    table: string,
    {options = DEFAULT_PAGE_SIZE_OPTIONS, fallback = options[0], onChange}: UsePageSizeOptions = {},
): [number, (size: number) => void] {
    const storageKey = pageSizeStorageKey(table);
    const [pageSize, setPageSizeState] = useState<number>(() => readStoredPageSize(storageKey, options, fallback));

    const setPageSize = useCallback((size: number): void => {
        if (!options.includes(size)) {
            return;
        }
        setPageSizeState(size);
        try {
            localStorage.setItem(storageKey, String(size));
        } catch {
            // выбор не переживёт перезагрузку, но текущая сессия должна работать
        }
        onChange?.(size);
        // options — литерал из модуля вызывающей страницы, стабилен по ссылке;
        // onChange страницы оборачивают в useCallback там, где это важно.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [storageKey, onChange]);

    return [pageSize, setPageSize];
}

export default usePageSize;
